import { Component, OnChanges, Input, OnDestroy, ElementRef, ViewChild, HostListener, asNativeElements } from '@angular/core';
import { InTransitDto, LocationServiceDto, LocationTypeDto } from 'src/shared/service-proxies/service-proxies';
import * as L from 'leaflet';
import * as R from 'leaflet-responsive-popup';
import { LocationClassNameHelper, LocationTypeNames } from 'src/app/enums/LocationTypeNames';
import { GeoHelper, GeoPosition } from 'src/app/helpers/geo';
import { InTransitModel } from '../models';
import { Router } from '@angular/router';

@Component({
    selector: 'app-widget-in-transit-map',
    templateUrl: './in-transit-map.component.html',
    styleUrls: ['./in-transit-map.component.scss'],
    standalone: false
})
export class InTransitMapComponent implements OnChanges, OnDestroy {
  // Inputs
  @Input()
  inTransit: InTransitDto[]
  
  @Input()
  services: LocationServiceDto[]

  @Input()
  onlyShowLocations: boolean = false;

  @Input()
  locationTypes: LocationTypeDto[];
  
  @ViewChild('mapdiv')
  mapDiv: ElementRef;

  // Public variables
  inTransitPage: InTransitModel[] = [];
  linesDrawn: boolean = false;
  locationTypesDisplay: LocationTypeDto[] = [];

  // Private variables
  private map;
  private defaultLocationTypeColour: string = "#fec107;";
  
  private mapLayer = L.tileLayer('http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key=AIzaSyChxd9LP-pHKK1qe1nPlECcK5iJb-a4hg8', {
      maxZoom: 18,
      minZoom: 3,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  });
  private drawLayer = L.layerGroup();

  constructor(private router: Router) { }

  public invalidateMap() {
    const map = this.map;
    setTimeout(function(){ map.invalidateSize(); }, 400);
  }

  ngOnChanges(changes) {
    if (changes.inTransit.firstChange === false) {
      this.updateView();
    }
  }

  ngAfterViewInit(): void { 
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.map ){
      this.map.eachLayer(function(layer){
          layer.remove();
      });
      this.map.remove();
      this.map = null;
      this.mapDiv.nativeElement.innerHTML = "";
    }
  }

  private initMap(): void {
    var control: InTransitMapComponent = this;
    this.map = L.map('map', {
      center: [ 53.55, -2.4333 ],
      zoom: 6,
      scrollWheelZoom: false
    });
    this.map.on('zoomend', function () {
      if (!this.linesDrawn) {
        control.drawLines();
      }
    });
    this.mapLayer.addTo(this.map);
    this.drawLayer.addTo(this.map);
    setTimeout(() => {
      this.map.invalidateSize(true);
    }, 1)
  }

  drawLines() {
    var control: InTransitMapComponent = this;
    if (!this.onlyShowLocations) {
        this.inTransitPage.forEach((inTransit, index) => {
        // Draw line
        let lineDestinationLat = inTransit.vehicleLatitude; 
        let lineDestinationLon =  inTransit.vehicleLongitude;
        if (inTransit.toLocationName !== null && inTransit.toLocationName  !== "") {
          lineDestinationLat = inTransit.toLocationLatitude;
          lineDestinationLon =  inTransit.toLocationLongitude;
        }
        var pointA = new L.LatLng(inTransit.fromLocationLatitude, inTransit.fromLocationLongitude);
        var pointB = new L.LatLng(lineDestinationLat, lineDestinationLon);
        var pointList = [pointA, pointB];
        var lineColor = inTransit.alert ? "red" : "black";
        var firstpolyline = new L.Polyline(pointList, {
            color: lineColor,
            className: "route-line",
            weight: 3,
            opacity: 1,
            smoothFactor: 1,
            customData: {
              control: this,
              movementId: inTransit.movementId
            }
            // @ts-ignore
        })//.arrowheads({ size: "10px"})
        .on(
          'click',
          function(event)
          {
            if (inTransit.movementId != null) {
              var layer = event.target;
              control.router.navigate([`/route-map/${ inTransit.movementId }`]);
            }
        });
        firstpolyline.addTo(this.drawLayer);
      })
      this.linesDrawn = true;
    }
  }

  updateView() {
    let page:  InTransitModel[] = [];
    this.inTransit.forEach(service => {
        page.push(this.MapDtoToModel(service));
    });

    this.inTransitPage = page;

    this.drawMap();
  }

  drawMap() {
    var control = this;
    let allLatLons: GeoPosition[] = [];
    let locationPinsAdded = new Set();

    // Clear  and re add draw layer
    this.linesDrawn = false;
    this.map.removeLayer(this.drawLayer);
    this.drawLayer = L.layerGroup();
    this.drawLayer.addTo(this.map);

    this.inTransitPage.forEach((inTransit, index) => {
      
      // Add From Location
      if (!locationPinsAdded.has(inTransit.fromLocationId)) {
        this.addLocationPin(inTransit.fromLocationId, inTransit.fromLocationTypeName, inTransit.fromLocationName, inTransit.fromLocationLatitude, inTransit.fromLocationLongitude, false),
        allLatLons.push({ lat: inTransit.fromLocationLatitude, lon: inTransit.fromLocationLongitude })
        locationPinsAdded.add(inTransit.fromLocationId);
      }

      if (inTransit.toLocationName !== null && inTransit.toLocationName  !== "") {
        // Add To Location
        if (!locationPinsAdded.has(inTransit.toLocationId)) {
          this.addLocationPin(inTransit.toLocationId, inTransit.toLocationTypeName, inTransit.toLocationName, inTransit.toLocationLatitude, inTransit.toLocationLongitude, true),
          allLatLons.push({ lat: inTransit.toLocationLatitude, lon: inTransit.toLocationLongitude })
          locationPinsAdded.add(inTransit.toLocationId);
        }
      } else {
        if (!this.onlyShowLocations && inTransit.toLocationLatitude == null) {
          // Add vehicle
          let icon = L.divIcon({
            className: 'custom-div-icon',
            html: "<div class='marker-pin vehicle'></div><i class='material-icons'>local_shipping</span></i>",
            iconSize: [30, 42],
            iconAnchor: [15, 36]
          });
          let popupContent = '<p>' + inTransit.vehicleName + '</p>';
          const popup = R.responsivePopup({ hasTip: false, autoPan: false, offset: [20, 20]}).setContent(popupContent);
          var marker = L.marker([inTransit.vehicleLatitude, inTransit.vehicleLongitude], { icon: icon }).bindPopup(popup).addTo(this.drawLayer);
          allLatLons.push({ lat: inTransit.vehicleLatitude, lon: inTransit.vehicleLongitude })
          

          var showTip = function() {
            this.unbindTooltip();
            if(!this.isPopupOpen()) this.bindPopup(popup, { minWidth: 280}).openPopup();
          }
          
          var hideTip = function() {
            this.unbindTooltip();
            if(this.isPopupOpen()) this.closePopup();
          }
          
          var router = this.router;
          var goToRoute = function() {
            router.navigate([`/route-map/${inTransit.movementId}`]);
          }

          marker.on('mouseover', showTip);
          marker.on('mouseout', hideTip);
          if (inTransit.movementId) {
            marker.on('click', goToRoute);
          }
        }
      }
    });

    // Centre map
    if (allLatLons.length > 0) {
      let centre = GeoHelper.GetCenterFromPositions(allLatLons);
      let zoom = GeoHelper.CalculateZoom(allLatLons, this.mapDiv.nativeElement.offsetHeight, this.mapDiv.nativeElement.offsetWidth);
      this.map.flyTo(new L.LatLng(centre.lat, centre.lon), zoom, {
        animate: true,
        duration: 1.5
      });
    }
  }

  addLocationPin(locationId, locationTypeName, locationName, locationLatitude, locationLongitude, vehicle: boolean) {
    if (!this.locationTypesDisplay.find(l => l.name == locationTypeName)) {
      var locationTypeNameDisplay = this.locationTypes.find(lt => lt.name == locationTypeName);
      this.locationTypesDisplay.push(locationTypeNameDisplay)
    }

    var className = LocationClassNameHelper.getLocationClassName(locationTypeName);
    var iconName = vehicle ? "local_shipping" : "home";
    let icon = L.divIcon({
      className: 'custom-div-icon',
      html: "<div class='marker-pin " + className + "' style='background-color: " + this.getlocationTypeColourByName(locationTypeName) + "'></div><i class='material-icons'>" + iconName + "</i>",
      iconSize: [30, 42],
      iconAnchor: [15, 36]
    });
    let popupContent = `<a class='routerlink' href="/location/${ locationId }">${ locationName }</a>`;
    
    if (this.services) {
      let table = "";
      let locationServices = this.services.filter(s => s.locationId == locationId);
      let vehicles = this.inTransit.filter(s => s.toLocationId == locationId);
      if (locationServices.length > 0) {
        table += "<table class='tooltip-table table table-striped'>"
        table += "<thead><tr><th>Vehicle</th><th>In</th><th>Out</th></tr></thead>"
        table += "<tbody>"
        locationServices.forEach(s => {
          table += `<tr><td class='vehicleName'>${ s.vehicleName }</td><td class='containerIn'>${ s.containerIn == null ? "" : s.containerIn }</td><td class='containerOut'>${ s.containerOut == null ? "" : s.containerOut }</td></tr>`;
        })
        table += "</tbody>"
        table += "</table>"
      }
      
      if (vehicles.length > 0) {
        var vehicleNames: string[] = [];
        vehicles.forEach(job => {
          if (!vehicleNames.find(v => v == job.vehicleName)) {
            vehicleNames.push(job.vehicleName);
          }
        })
        table += "<table class='tooltip-table table table-striped'>"
        table += "<thead><tr><th>Vehicles At Site</th></tr></thead>"
        table += "<tbody>"
        vehicleNames.forEach(s => {
          table += `<tr><td class='vehicleName'>${ s }</td></tr>`;
        })
        table += "</tbody>"
        table += "</table>"
      }
      if (locationTypeName !== LocationTypeNames.Depot) {
        popupContent += table;
      }
    }
    const popup = R.responsivePopup({ hasTip: false, autoPan: false, offset: [20, 20] }).setContent(popupContent);
    var marker = L.marker([locationLatitude, locationLongitude], { icon: icon, riseOnHover: true }).bindPopup(popup).addTo(this.drawLayer);

    var showTip = function() {
      this.unbindTooltip();
      
      if(!this.isPopupOpen()) this.bindPopup(popup).openPopup();
    }
    
    var hideTip = function() {
      this.unbindTooltip();
      if(this.isPopupOpen()) this.closePopup();
    }
    
    var router = this.router;
    var goToLocation = function() {
      router.navigate([`/location/${locationId}`]);
    }

    marker.on('mouseover', showTip);
    marker.on('mouseout', hideTip);
    marker.on('click', goToLocation);
  }

  MapDtoToModel(dto: InTransitDto): InTransitModel {
    let model: InTransitModel = dto;
    return model;
  }

  getlocationTypeColourByName(type: string): string {
    var typeItem = this.locationTypes.find(t => t.name == type);
    if (type) {
      return typeItem.colour;
    }

    return this.defaultLocationTypeColour;
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: Event): void {
    if (event.target instanceof HTMLAnchorElement) {
      const element = event.target as HTMLAnchorElement;
      if (element.className === 'routerlink') {
        event.preventDefault();
        const route = element.getAttribute('href');
        if (route) {
          this.router.navigate([`/${route}`]);
        }
      }
    }
  }
}